// utils/cmsAdminApi.js
// ──────────────────────────────────────────────────────────────────────────────
// Authenticated API client for the institute admin CMS panel.
// All endpoints are tenant-scoped server-side via req.user.instituteId.
// ──────────────────────────────────────────────────────────────────────────────

import apiClient from './apiClient';

// ─── Blogs ──────────────────────────────────────────────────────────────────
export const listAdminBlogs = (params = {}) =>
  apiClient.get('/cms/blogs', { params }).then(r => r.data);

export const getAdminBlog = (id) =>
  apiClient.get(`/cms/blogs/${id}`).then(r => r.data);

export const createBlog = (payload) =>
  apiClient.post('/cms/blogs', payload).then(r => r.data);

export const updateBlog = (id, payload) =>
  apiClient.put(`/cms/blogs/${id}`, payload).then(r => r.data);

export const deleteBlog = (id) =>
  apiClient.delete(`/cms/blogs/${id}`).then(r => r.data);

// ─── Testimonials ───────────────────────────────────────────────────────────
export const listAdminTestimonials = () =>
  apiClient.get('/cms/testimonials').then(r => r.data);

export const createTestimonial = (payload) =>
  apiClient.post('/cms/testimonials', payload).then(r => r.data);

export const updateTestimonial = (id, payload) =>
  apiClient.put(`/cms/testimonials/${id}`, payload).then(r => r.data);

export const deleteTestimonial = (id) =>
  apiClient.delete(`/cms/testimonials/${id}`).then(r => r.data);

// ─── Team (website-only mentor / trainer profiles) ──────────────────────────
// ⚠️ A profile here is NOT an account: nothing on these endpoints creates a
// login, sends an invite or consumes an instructor seat. LMS access is still
// granted only by adding an instructor under People → Instructors.
export const listTeamMembers = () =>
  apiClient.get('/cms/team').then(r => r.data);

export const createTeamMember = (payload) =>
  apiClient.post('/cms/team', payload).then(r => r.data);

export const updateTeamMember = (id, payload) =>
  apiClient.put(`/cms/team/${id}`, payload).then(r => r.data);

export const deleteTeamMember = (id) =>
  apiClient.delete(`/cms/team/${id}`).then(r => r.data);

/** The whole visible order in one write — ids top to bottom. */
export const reorderTeamMembers = (ids) =>
  apiClient.put('/cms/team/reorder', { ids }).then(r => r.data);

/** Instructors whose details can be copied into a profile, `imported` flagged. */
export const listTeamInstructorCandidates = () =>
  apiClient.get('/cms/team/instructor-candidates').then(r => r.data);

/** Copy instructors into website profiles — once; the copies are independent. */
export const importInstructorsToTeam = (instructorIds) =>
  apiClient.post('/cms/team/import', { instructorIds }).then(r => r.data);

// ─── Image upload ───────────────────────────────────────────────────────────
// kind ∈ 'blog-cover' | 'testimonial-photo' | 'team-photo' | 'page-media'
//        | 'hero-background' | 'cms-favicon'
export const uploadCmsImage = (file, kind = 'page-media') => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', kind);
  return apiClient
    .post('/cms/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(r => r.data);
};

export const deleteCmsImage = (key) =>
  apiClient.delete('/cms/image', { data: { key } }).then(r => r.data);

// ─── Page Content ───────────────────────────────────────────────────────────
export const getPageContent = () =>
  apiClient.get('/cms/page-content').then(r => r.data);

export const updatePageContent = (content) =>
  apiClient.put('/cms/page-content', { content }).then(r => r.data);

// Every course with whether it is published — the website's course pickers.
// Returns { success, data: [{ id, name, slug, courseType, isPublished }] }.
export const listWebsiteCourses = () =>
  apiClient.get('/cms/courses').then(r => r.data);

// ─── Header, footer, appearance & custom code ───────────────────────────────
// Returns { customCode, footer, navbar, theme }.
export const getCmsCustomization = () =>
  apiClient.get('/institute/cms-customization').then(r => r.data);

export const updateCmsCustomization = (payload) =>
  apiClient.put('/institute/cms-customization', payload).then(r => r.data);

// ─── SEO settings (institute-wide) ──────────────────────────────────────────
export const getSeoSettings = () =>
  apiClient.get('/cms/seo').then(r => r.data);

export const updateSeoSettings = (seo) =>
  apiClient.put('/cms/seo', { seo }).then(r => r.data);

// ─── CMS website settings (Institute settings) ──────────────────────────────
// Returns { cmsEnabled, layoutPreset, layoutBrandingMode }.
export const getCmsSettings = () =>
  apiClient.get('/institute/cms-settings').then(r => r.data);

// Patch any subset of the CMS website settings. Pass an object with only
// the fields you want to update — other fields stay as-is server-side.
// Back-compat: passing a plain boolean treats it as { cmsEnabled }.
export const updateCmsSettings = (patch) => {
  const body = typeof patch === 'boolean' ? { cmsEnabled: patch } : (patch || {});
  return apiClient.patch('/institute/cms-settings', body).then(r => r.data);
};

// ─── AI content generation ─────────────────────────────────────────────────
export const aiGeneratePage = (payload) =>
  apiClient.post('/cms/ai/generate-page', payload).then(r => r.data);

export const aiGenerateBlog = (payload) =>
  apiClient.post('/cms/ai/generate-blog', payload).then(r => r.data);

export const aiGenerateCourseMarketing = (payload) =>
  apiClient.post('/cms/ai/generate-course-marketing', payload).then(r => r.data);

export const aiGenerateSyllabus = (payload) =>
  apiClient.post('/cms/ai/generate-syllabus', payload).then(r => r.data);

export const aiGenerateSeo = (payload) =>
  apiClient.post('/cms/ai/generate-seo', payload).then(r => r.data);

// `payload` carries the admin's model pick ({ model, reasoningEffort? }). The
// audit reads the whole site — pages, courses and blogs in one prompt — which
// is the longest input of any CMS generation, so the model matters most here.
export const aiAuditSeo = (payload = {}) =>
  apiClient.post('/cms/ai/audit-seo', payload).then(r => r.data);

export const getCmsAiUsage = (recentPage = 1, recentLimit = 10) =>
  apiClient.get('/cms/ai/usage', { params: { recentPage, recentLimit } }).then(r => r.data);
